import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  BucketDataPoint,
  DataPoint,
  useControlMonitorQuery,
} from "@/queries/control-monitor";
import { OverlaySchema } from "../overlays";

const getPath = (data: DataPoint[], id: string | undefined): string[] => {
  if (!id) {
    return [];
  }

  for (const item of data) {
    if (item.id === id) {
      return [item.id];
    }

    const children = getPath(item.children, id);

    if (children.length > 0) {
      return [item.id, ...children];
    }
  }
  return [];
};

const isPolicyMisconfigurations = (item: BucketDataPoint) =>
  item.id.endsWith("-policy-misconfigurations");

export const isLeaf = (item: DataPoint): boolean => item.children.length === 0;

interface SearchParams {
  threat_id?: string;
  technique?: string;
  overlay?: OverlaySchema;
}

const EMPTY_ARRAY: never[] = [];

export const useControlMonitorNavigation = () => {
  const controlMonitorQuery = useControlMonitorQuery();
  const data = controlMonitorQuery.data?.dataPoints ?? EMPTY_ARRAY;

  const navigate = useNavigate();
  const router = useRouterState();

  const activeId = router.location.search.active_id;
  const path = useMemo(() => getPath(data, activeId), [data, activeId]);

  const view = (item: DataPoint, params: SearchParams = {}) => {
    const pathIndex = path.indexOf(item.id);
    const isInPath = pathIndex !== -1;
    const isActiveLeaf =
      pathIndex === path.length - 1 && item.children.length === 0;

    if (isInPath && !isActiveLeaf) {
      return navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          active_id: pathIndex === 0 ? "root" : path.at(pathIndex - 1),
          ...params,
        }),
      });
    }

    if (item.type === "disconnected-control") {
      return navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          active_id: item.id,
          overlay: ["integration", { control: item.control }],
          ...params,
        }),
      });
    }

    if (item.type === "bucket" && isPolicyMisconfigurations(item)) {
      const { id, control, instanceId, misconfigurationCount } = item;
      return navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          active_id: id,
          overlay: [
            "policies",
            {
              instanceId: instanceId,
              control,
              filter: misconfigurationCount > 0 ? "failing" : "all",
            },
          ],
          ...params,
        }),
      });
    }

    if (item.type === "control-category-failure") {
      return navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          active_id: item.id,
          overlay: [
            "control-category-failures",
            {
              category: item.category,
              partnerEvent: item.failure,
              filters: [],
            },
          ],
          ...params,
        }),
      });
    }

    if (item.type === "control-failure") {
      return navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          active_id: item.id,
          overlay: [
            "control-failures",
            {
              instanceId: item.instanceId,
              control: item.control,
              partnerEvent: item.failure,
              filters: [],
            },
          ],
          ...params,
        }),
      });
    }

    if (item.type === "connected-control" && isLeaf(item)) {
      return navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          active_id: item.id,
          overlay: [
            "control-endpoints",
            {
              instanceId: item.instanceId,
              control: item.control,
              category: item.category,
              filters: [],
            },
          ],
          ...params,
        }),
      });
    }

    return navigate({
      to: "/control-monitor",
      search: (prev) => ({ ...prev, active_id: item.id, ...params }),
    });
  };

  return { activeId, path, view };
};
